import { useState, type JSX, useEffect } from 'react'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { Password } from 'primereact/password'
import { InputSwitch } from 'primereact/inputswitch'
import { Steps } from 'primereact/steps'
import { confirmDialog } from 'primereact/confirmdialog'

import type { TenantAdmin, TenantAdminFormData } from '../../hooks/useTenantAdmins.js'
import { api } from '../../api/client.js'
import { useTenantPrivileges } from '../../hooks/useTenantPrivileges.js'

const ADMIN_STEPS = [
  { label: 'Account' },
  { label: 'Personal' },
] as const

interface TenantAdminDrawerProps {
  open: boolean
  onClose: () => void
  mode: 'create' | 'edit' | 'view'
  admin?: TenantAdmin | undefined
  onSave: (data: TenantAdminFormData) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  loading: boolean
}

export default function TenantAdminDrawer({
  open,
  onClose,
  mode,
  admin,
  onSave,
  onDelete,
  loading
}: TenantAdminDrawerProps): JSX.Element {
  const [activeStep, setActiveStep] = useState(0)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [originalData, setOriginalData] = useState<TenantAdminFormData | null>(null)
  const { list: listPrivileges } = useTenantPrivileges()

  const [tenants, setTenants] = useState<any[]>([])
  const [privileges, setPrivileges] = useState<any[]>([])

  const [formData, setFormData] = useState<TenantAdminFormData>({
    username: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    password: '',
    mobileNumber: '',
    landlineNumber: '',
    status: true,
    domainId: '',
    privilegeId: ''
  })

  useEffect(() => {
    if (open) {
      setActiveStep(0)
      setFieldErrors({})
      setHasSubmitted(false)
      // Fetch tenants directly from API
      api.get<{ tenants: any[] }>('/tenants?limit=100&offset=0').then((res: { tenants: any[] }) => setTenants(res.tenants))
      listPrivileges(100, 0).then((res: { tenantPrivileges: any[] }) => setPrivileges(res.tenantPrivileges))
      
      if (admin && mode !== 'create') {
        const initialData = {
            username: admin.username || '',
            firstName: admin.firstName || '',
            lastName: admin.lastName || '',
            email: admin.email,
            password: '', // Don't populate password for security
            middleName: admin.middleName || '',
            mobileNumber: admin.mobileNumber || '',
            landlineNumber: admin.landlineNumber || '',
            status: admin.status,
            domainId: admin.tenantId || '',
            privilegeId: admin.privilegeName ? privileges.find(p => p.name === admin.privilegeName)?.id : ''
        }
        setFormData(initialData)
        setOriginalData(initialData)
      } else {
        setFormData({
            username: '',
            firstName: '',
            middleName: '',
            lastName: '',
            email: '',
            password: '',
            mobileNumber: '',
            landlineNumber: '',
            status: true,
            domainId: '',
            privilegeId: ''
        })
      }
    }
  }, [open, admin, mode])

  const handleGeneratePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
    const pass = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
    setFormData(prev => ({ ...prev, password: pass }))
  }

  const hasUnsavedChanges = (): boolean => {
    if (!originalData) return false
    // Compare current formData with originalData (excluding password which is always empty in edit mode)
    const { password: _, ...currentWithoutPassword } = formData
    const { password: __, ...originalWithoutPassword } = originalData
    return JSON.stringify(currentWithoutPassword) !== JSON.stringify(originalWithoutPassword)
  }

  const handleNextStep = (e?: React.MouseEvent): void => {
    e?.preventDefault() // Prevent form submission
    setFieldErrors({}) // Clear all errors before validation
    setHasSubmitted(false) // Reset submitted flag when moving to next step
    
    const errors: Record<string, string> = {}
    
    // Validate account tab fields
    if (!formData.username || !formData.username.trim()) {
      errors.username = 'Username is required'
    }
    if (!formData.password && mode === 'create') {
      errors.password = 'Password is required'
    }
    if (!formData.domainId) {
      errors.domainId = 'Tenant is required'
    }
    if (!formData.privilegeId) {
      errors.privilegeId = 'Admin privileges are required'
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    
    setActiveStep(1)
  }

  const handleBackStep = (): void => {
    setFieldErrors({})
    setHasSubmitted(false)
    setActiveStep(0)
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setHasSubmitted(true)
    setFieldErrors({}) // Clear all errors before validation
    
    const errors: Record<string, string> = {}
    
    // Validate personal tab fields
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required'
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required'
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Invalid email format'
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    
    try {
      // In edit mode, don't send password if it's empty
      const dataToSend: any = { ...formData }
      if (mode === 'edit' && !formData.password) {
        delete dataToSend.password
      }
      await onSave(dataToSend)
      setFieldErrors({}) // Clear all errors after successful submission
      setHasSubmitted(false)
    } catch (error) {
      if (error instanceof Error) {
        // Parse error message for better display
        const errorMsg = error.message
        
        // Check for duplicate key violations - check multiple patterns
        const isDuplicateEmail = errorMsg.includes('email') && (
          errorMsg.includes('duplicate key') || 
          errorMsg.includes('unique constraint') || 
          errorMsg.includes('platform_admins_email_unique') ||
          errorMsg.includes('duplicate key value violates unique constraint')
        )
        
        const isDuplicateUsername = errorMsg.includes('username') && (
          errorMsg.includes('duplicate key') || 
          errorMsg.includes('unique constraint')
        )
        
        if (isDuplicateEmail) {
          setFieldErrors({ email: 'Email already exists' })
        } else if (isDuplicateUsername) {
          setFieldErrors({ username: 'Username already exists' })
        } else if (errorMsg.includes('INTERNAL_ERROR') || errorMsg.includes('Failed query')) {
          // For backend errors, assume it's a duplicate email if email is in the params
          if (errorMsg.includes('email')) {
            setFieldErrors({ email: 'Email already exists' })
          } else {
            setFieldErrors({ general: 'Failed to save admin. Please check your input and try again.' })
          }
        } else if (errorMsg.includes('firstName')) {
          setFieldErrors({ firstName: 'First name is required' })
        } else if (errorMsg.includes('lastName')) {
          setFieldErrors({ lastName: 'Last name is required' })
        } else if (errorMsg.includes('email') && errorMsg.includes('invalid')) {
          setFieldErrors({ email: 'Invalid email format' })
        } else {
          // Show a generic user-friendly error instead of raw SQL
          setFieldErrors({ general: 'Failed to save admin. Please check your input and try again.' })
        }
      }
    }
  }

  const handleDelete = (): void => {
    if (!admin || !onDelete) return
    
    confirmDialog({
      message: `Are you sure you want to delete admin "${admin.firstName} ${admin.lastName}"?`,
      header: 'Delete Admin',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await onDelete(admin.id)
          onClose()
        } catch (error) {
          console.error('Failed to delete admin:', error)
        }
      }
    })
  }

  const isReadOnly = mode === 'view'

  const accountTab = () => (
    <div className="space-y-4">
      {/* Account Status */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Account Status</h3>
        </div>
        <div className="p-5">
          <div className={`flex items-center justify-between p-4 rounded-sm border transition-all ${formData.status ? 'bg-emerald-50/30 border-emerald-100' : 'bg-gray-50/50 border-gray-100'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${formData.status ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                <i className={formData.status ? 'pi pi-check text-sm' : 'pi pi-times text-sm'} />
              </div>
              <div>
                <div className="font-semibold text-gray-800 text-sm">
                  {formData.status ? 'Active Status' : 'Inactive Status'}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {formData.status ? 'Admin is active and can log in' : 'Admin is inactive and cannot log in'}
                </div>
              </div>
            </div>
            <InputSwitch
              checked={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.value ?? false })}
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>

      {/* Account Details */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Account Details</h3>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Username <span className="text-red-500">*</span></label>
            <InputText
              value={formData.username}
              onChange={e => {
                setFormData({ ...formData, username: e.target.value })
                if (fieldErrors.username) setFieldErrors(prev => ({ ...prev, username: '' }))
              }}
              disabled={isReadOnly}
              placeholder="Enter username"
              pt={{
                root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' },
              } as any}
            />
            {fieldErrors.username && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.username}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Password <span className="text-red-500">*</span></label>
            <div className="flex gap-2 items-center">
              <Password
                value={formData.password}
                onChange={e => {
                  setFormData({ ...formData, password: e.target.value })
                  if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: '' }))
                }}
                disabled={isReadOnly}
                toggleMask
                feedback={false}
                placeholder={mode === 'edit' ? "Leave empty to keep current" : "Enter password"}
                pt={{
                  root: { className: 'w-full' },
                  input: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' },
                  showIcon: { className: 'text-gray-400 hover:text-[#f6931c] transition-colors' },
                  hideIcon: { className: 'text-gray-400 hover:text-[#f6931c] transition-colors' },
                } as any}
              />
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-2.5 rounded-xl transition-all"
              >
                <i className="pi pi-key text-xs" />
                Generate
              </button>
            </div>
            <p className="text-xs text-gray-400">Generate a secure password or enter your own</p>
            {fieldErrors.password && <p className="text-xs text-red-500">{fieldErrors.password}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Tenant <span className="text-red-500">*</span></label>
            <Dropdown
              value={formData.domainId}
              onChange={e => {
                setFormData({ ...formData, domainId: e.value })
                if (fieldErrors.domainId) setFieldErrors(prev => ({ ...prev, domainId: '' }))
              }}
              options={tenants}
              optionLabel="name"
              optionValue="id"
              disabled={isReadOnly}
              placeholder="Select tenant"
              className="w-full"
              filter
              filterPlaceholder="Search tenants..."
              appendTo={document.body}
              pt={{
                root: { className: 'w-full !border !border-gray-200 !rounded-xl' },
                input: { className: '!rounded-xl !border-0 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none' },
                trigger: { className: '!rounded-xl !bg-gray-50/50 w-11 text-gray-400' },
                panel: { className: '!border !border-gray-200 !rounded-xl shadow-lg' },
              } as any}
            />
            {fieldErrors.domainId && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.domainId}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Admin Privileges <span className="text-red-500">*</span></label>
            <Dropdown
              value={formData.privilegeId}
              onChange={e => {
                setFormData({ ...formData, privilegeId: e.value })
                if (fieldErrors.privilegeId) setFieldErrors(prev => ({ ...prev, privilegeId: '' }))
              }}
              options={privileges}
              optionLabel="name"
              optionValue="id"
              disabled={isReadOnly}
              placeholder="Select privileges"
              className="w-full"
              filter
              filterPlaceholder="Search privileges..."
              appendTo={document.body}
              pt={{
                root: { className: 'w-full !border !border-gray-200 !rounded-xl' },
                input: { className: '!rounded-xl !border-0 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none' },
                trigger: { className: '!rounded-xl !bg-gray-50/50 w-11 text-gray-400' },
                panel: { className: '!border !border-gray-200 !rounded-xl shadow-lg' },
              } as any}
            />
            {fieldErrors.privilegeId && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.privilegeId}</p>}
          </div>
        </div>
      </div>
    </div>
  )

  const personalTab = () => (
    <div className="space-y-5">
      {/* Personal Details */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Personal Details</h3>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">First Name <span className="text-red-500">*</span></label>
            <InputText
              value={formData.firstName}
              onChange={e => {
                setFormData({ ...formData, firstName: e.target.value })
                if (fieldErrors.firstName) setFieldErrors(prev => ({ ...prev, firstName: '' }))
              }}
              disabled={isReadOnly}
              placeholder="Enter first name"
              pt={{
                root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' },
              } as any}
            />
            {hasSubmitted && fieldErrors.firstName && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.firstName}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Middle Name</label>
            <InputText
              value={formData.middleName}
              onChange={e => setFormData({ ...formData, middleName: e.target.value })}
              disabled={isReadOnly}
              placeholder="Enter middle name"
              pt={{
                root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' },
              } as any}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Last Name <span className="text-red-500">*</span></label>
            <InputText
              value={formData.lastName}
              onChange={e => {
                setFormData({ ...formData, lastName: e.target.value })
                if (fieldErrors.lastName) setFieldErrors(prev => ({ ...prev, lastName: '' }))
              }}
              disabled={isReadOnly}
              placeholder="Enter last name"
              pt={{
                root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' },
              } as any}
            />
            {hasSubmitted && fieldErrors.lastName && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.lastName}</p>}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Contact Information</h3>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Email Address <span className="text-red-500">*</span></label>
            <InputText
              type="email"
              value={formData.email}
              onChange={e => {
                setFormData({ ...formData, email: e.target.value })
                if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }))
              }}
              disabled={isReadOnly || mode === 'edit'}
              placeholder="Enter email address"
              pt={{
                root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' },
              } as any}
            />
            {hasSubmitted && fieldErrors.email && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.email}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Mobile Number</label>
            <InputText
              value={formData.mobileNumber}
              onChange={e => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 15)
                setFormData({ ...formData, mobileNumber: value })
              }}
              disabled={isReadOnly}
              placeholder="Enter mobile number"
              maxLength={15}
              pt={{
                root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' },
              } as any}
            />
            <p className="text-xs text-gray-400">Optional, max 15 digits</p>
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <label className="text-sm font-semibold text-gray-700">Landline Number</label>
            <InputText
              value={formData.landlineNumber}
              onChange={e => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 15)
                setFormData({ ...formData, landlineNumber: value })
              }}
              disabled={isReadOnly}
              placeholder="Enter landline number"
              maxLength={15}
              pt={{
                root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' },
              } as any}
            />
            <p className="text-xs text-gray-400">Optional, max 15 digits</p>
          </div>
        </div>
      </div>
    </div>
  )

  const renderStepContent = (): JSX.Element => {
    switch (activeStep) {
      case 0:
        return accountTab()
      case 1:
        return personalTab()
      default:
        return <></>
    }
  }

  return (
    <>
      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />
          
          {/* Drawer Panel */}
          <aside className="fixed right-0 top-0 z-[9999] flex h-[100vh] w-full max-w-3xl flex-col bg-white shadow-2xl">
            <form onSubmit={handleSubmit} className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
                    <i className="pi pi-user text-white text-sm" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">
                      {mode === 'create' ? 'New Tenant Admin' : mode === 'edit' ? 'Edit Tenant Admin' : 'View Tenant Admin'}
                    </h2>
                    <p className="text-xs text-gray-400">Step {activeStep + 1} of {ADMIN_STEPS.length}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all"
                >
                  <i className="pi pi-times" aria-hidden="true" />
                </button>
              </div>

              {/* Step indicator */}
              <div className="border-b border-gray-100 px-6 py-3 shrink-0 bg-white">
                <Steps
                  model={ADMIN_STEPS as unknown as { label: string }[]}
                  activeIndex={activeStep}
                  onSelect={(e) => {
                    setFieldErrors({})
                    setHasSubmitted(false)
                    setActiveStep(e.index)
                  }}
                  readOnly={false}
                  pt={{
                    root: { className: 'w-full' },
                    menu: { className: 'flex flex-row gap-2' },
                    item: { className: 'flex-1' },
                    label: { className: 'text-sm font-medium text-gray-400' },
                    active: { className: 'text-[#3454cf] font-semibold' }
                  } as any}
                />
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                {fieldErrors.general && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-5">
                    <div className="flex items-center gap-2">
                      <i className="pi pi-exclamation-triangle text-red-500 text-sm" />
                      <span className="text-sm font-medium text-red-700">{fieldErrors.general}</span>
                    </div>
                  </div>
                )}
                {renderStepContent()}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 bg-white shrink-0">
                {activeStep === 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
                    >
                      Next
                      <i className="pi pi-arrow-right text-xs" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleBackStep}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
                      >
                        <i className="pi pi-arrow-left text-xs" />
                        Back
                      </button>
                      {mode !== 'create' && onDelete && (
                        <button
                          type="button"
                          onClick={handleDelete}
                          className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-red-200 text-red-600 hover:bg-red-50 transition-all"
                        >
                          <i className="pi pi-trash text-xs" />
                          Delete
                        </button>
                      )}
                    </div>
                    {!isReadOnly && (
                      <button
                        type="submit"
                        disabled={loading || (mode === 'edit' && !hasUnsavedChanges())}
                        className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
                      >
                        {loading ? <i className="pi pi-spin pi-spinner text-xs" /> : <i className="pi pi-check text-xs" />}
                        {mode === 'create' ? 'Create Admin' : 'Save Changes'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </form>
          </aside>
        </>
      )}
    </>
  )
}
